import { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResult,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';

import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';

import { formName, checkOrder, formUiSchema } from './accessorFunctions';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceJobFormList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-job-forms`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOBFORMS_LIST_BREADCRUMB',
        defaultMessage: 'List Job Forms',
      }),
      icon: IconLibrary.ServiceJobForms,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_NEW_SERVICEJOBFORM_STACKABLE_BUTTON',
        defaultMessage: 'Create Job Form',
      }),
      Icon: IconLibrary.ServiceJobForm,
      variant: 'outlined',
      onClick: () => {
        navigate('/servicejobforms/create');
      },
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    const searchParams = Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
    const urlParams = JSON.stringify(searchParams);
    return urlParams;
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    navigate(`/servicejobforms?filters=${requestData}&amp;page=0`);
  };

  const preProcessSearchValues = searchedParams => {
    const { ...other } = searchedParams;
    return {
      ...other,
    };
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceJobFormList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      formName,
      checkOrder,
      formUiSchema,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'DESC',
    sortBy: 'checkOrder',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      navigate(`/servicejobforms`);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'status':
        return value === 'true' ? 'Active' : 'In-Active';

      default:
        return value;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResult {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SERVICEJOBFORMSEARCH_HEADING',
            defaultMessage: 'Search Job Forms',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: 'SERVICEJOBFORMLIST_HEADING',
          defaultMessage: 'Job Forms',
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceJobFormList);
